const truncateToMinute = (dateStr: string): number => {
  const date = new Date(dateStr);
  date.setSeconds(0, 0);
  return date.getTime();
};

/**
 * Recalcula o status de um evento aplicando tolerância de 1 minuto.
 *
 * Regra: Só considera atraso a partir do minuto seguinte.
 * Ex: Planejado 12:00, Real 12:00:59 → COMPLETED (não COMPLETED_LATE)
 *
 * IMPORTANTE: Preserva `originalStatus` para manter informação de atraso
 * mesmo quando a tolerância corrige o status visual.
 *
 * @param event - Evento da timeline com status calculado pelo backend
 * @returns Evento com status corrigido se necessário e originalStatus preservado
 */
export const applyStatusTolerance = <T extends {
  status?: Sentinel.Enumerators.ProcessExecutionStatus;
  originalStatus?: Sentinel.Enumerators.ProcessExecutionStatus;
  executionStart?: string | null;
  executionEnd?: string | null;
  plannedExecutionStart?: string;
  plannedExecutionEnd?: string;
}>(event: T): T => {
  const {
    status,
    executionStart,
    executionEnd,
    plannedExecutionStart,
    plannedExecutionEnd,
  } = event;

  let correctedStatus = status;
  // Preserva o status original do backend para lógica de atraso
  const originalStatus = event.originalStatus ?? status;

  // Corrige COMPLETED_LATE → COMPLETED se estiver no mesmo minuto
  if (status === "COMPLETED_LATE" && executionEnd && plannedExecutionEnd) {
    const endMinute = truncateToMinute(executionEnd);
    const plannedEndMinute = truncateToMinute(plannedExecutionEnd);

    if (endMinute <= plannedEndMinute) {
      correctedStatus = "COMPLETED" as typeof status;
    }
  }

  // Corrige STARTED_LATE → STARTED se estiver no mesmo minuto
  if (status === "STARTED_LATE" && executionStart && plannedExecutionStart) {
    const startMinute = truncateToMinute(executionStart);
    const plannedStartMinute = truncateToMinute(plannedExecutionStart);

    if (startMinute <= plannedStartMinute) {
      correctedStatus = "STARTED" as typeof status;
    }
  }

  return { ...event, status: correctedStatus, originalStatus };
};

/**
 * Aplica a tolerância de status em uma lista de eventos.
 * Usado no endpoint /api/timeline/status
 */
export const applyStatusToleranceToEvents = (
  data: Sentinel.Timeline.StatusTimelineResponse
): Sentinel.Timeline.StatusTimelineResponse => {
  if (!data?.events) return data;

  return {
    ...data,
    events: data.events.map((event) => applyStatusTolerance(event)),
    lastEvent: data.lastEvent
      ? applyStatusTolerance(data.lastEvent)
      : data.lastEvent,
  };
};

/**
 * Aplica a tolerância de status na estrutura de management (Gantt).
 * Usado no endpoint /api/timeline/management
 */
export const applyStatusToleranceToManagement = (
  data: Sentinel.Timeline.New
): Sentinel.Timeline.New => {
  if (!data?.lanedEvents) return data;

  return {
    ...data,
    lanedEvents: data.lanedEvents.map((lane) => ({
      ...lane,
      groupedEvents: lane.groupedEvents?.map((group) => ({
        ...group,
        events: group.events?.map((event) => applyStatusTolerance(event)),
      })),
    })),
  };
};
